import React, { useState, useContext } from "react";
import { AuthContext } from "../../src/contexts/auth";
import { useNavigate } from "react-router-dom";
import {
  Box, Flex, FormControl, FormLabel, Input, Button, Image, Text,
  Alert, AlertIcon, AlertTitle, HStack, PinInput, PinInputField, VStack
} from "@chakra-ui/react";
import Preloader from "../components/Preloader";
import { solicitarCodigoWhatsApp, validarCodigoWhatsApp } from "../services/api";

const Login = () => {
  const { login, loginWithToken } = useContext(AuthContext);
  const navigate = useNavigate();

  // Estados gerais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login com senha
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Login com código WhatsApp
  const [mode, setMode] = useState("password"); // "password" | "whatsapp-email" | "whatsapp-code"
  const [whatsEmail, setWhatsEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [sendingCode, setSendingCode] = useState(false);

  // ── Login com senha ──
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res === false) {
        setError("Email ou senha incorretos!");
      } else if (res?.erro) {
        setError(res.erro);
      }
      // Sempre limpa loading — se o navigate ocorreu, o componente já desmontou
      // e este setState é ignorado; se não ocorreu, desbloqueia a UI.
    } catch {
      setError("Erro ao conectar");
    } finally {
      setLoading(false);
    }
  };

  // ── Solicitar código ──
  const handleSolicitarCodigo = async (e) => {
    e.preventDefault();
    setError("");
    setSendingCode(true);
    const res = await solicitarCodigoWhatsApp(whatsEmail);
    setSendingCode(false);

    if (res.status === "ok") {
      setPhoneMasked(res.phone);
      setMode("whatsapp-code");
    } else {
      setError(res.message || "Erro ao enviar código");
    }
  };

  // ── Validar código ──
  const handleValidarCodigo = async (value) => {
    setCodigo(value);
    if (value.length !== 6) return;

    setError("");
    setLoading(true);
    try {
      const res = await validarCodigoWhatsApp(whatsEmail, value);

      if (res.status === "ok" && res.data) {
        loginWithToken(res.data.token, res.data.user);
      } else {
        setError(res.message || "Código inválido ou expirado");
        setCodigo("");
      }
    } catch {
      setError("Erro ao conectar");
      setCodigo("");
    } finally {
      // Sempre limpa loading — se navigate ocorreu o componente desmontou e isso é noop
      setLoading(false);
    }
  };

  // ── Reenviar código ──
  const handleReenviar = async () => {
    setError("");
    setSendingCode(true);
    const res = await solicitarCodigoWhatsApp(whatsEmail);
    setSendingCode(false);
    if (res.status === "ok") {
      setPhoneMasked(res.phone);
      setError("");
    } else {
      setError(res.message || "Erro ao reenviar");
    }
  };

  if (loading) return <Preloader />;

  return (
    <Flex justify="center" align="center" h="100vh" background='url("/wpp_truck.jpg")' backgroundSize="cover">
      <Box w="400px" mx={1} p={6} borderRadius="md" boxShadow="dark-lg" backdropFilter="blur(5px)">
        <Box textAlign="center" mb={2}>
          <Box display="inline-block" p={4}>
            <Image src="/TAGANOBG.png" alt="Logo" />
          </Box>
        </Box>

        {/* ── Login com Senha ── */}
        {mode === "password" && (
          <form onSubmit={handlePasswordLogin}>
            <Box textAlign="center" mb={4}>
              <Text color="white" fontSize="xl" fontWeight="bold">Login</Text>
            </Box>
            {error && (
              <Alert status="error" borderRadius="md" mb={3}>
                <AlertIcon /><AlertTitle fontSize="sm">{error}</AlertTitle>
              </Alert>
            )}
            <FormControl isRequired>
              <FormLabel color="white">Email</FormLabel>
              <Input color="white" type="email" placeholder="Digite o e-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FormControl>
            <FormControl isRequired mt={4}>
              <FormLabel color="white">Senha</FormLabel>
              <Input color="white" type="password" placeholder="Digite a sua senha" value={password} onChange={(e) => setPassword(e.target.value)} />
            </FormControl>
            <Button bg="#F68633" color="white" _hover={{ bg: "#d4702a" }} mt={5} type="submit" isFullWidth>
              Entrar
            </Button>
            <Box textAlign="center" mt={4}>
              <Text
                color="gray.300" fontSize="sm" cursor="pointer"
                _hover={{ color: "white", textDecoration: "underline" }}
                onClick={() => { setMode("whatsapp-email"); setError(""); }}
              >
                Entrar com código via WhatsApp
              </Text>
            </Box>
          </form>
        )}

        {/* ── Solicitar Código ── */}
        {mode === "whatsapp-email" && (
          <form onSubmit={handleSolicitarCodigo}>
            <Box textAlign="center" mb={4}>
              <Text color="white" fontSize="xl" fontWeight="bold">Login via WhatsApp</Text>
              <Text color="gray.300" fontSize="xs" mt={1}>
                Enviaremos um código de 6 dígitos para o WhatsApp cadastrado
              </Text>
            </Box>
            {error && (
              <Alert status="error" borderRadius="md" mb={3}>
                <AlertIcon /><AlertTitle fontSize="sm">{error}</AlertTitle>
              </Alert>
            )}
            <FormControl isRequired>
              <FormLabel color="white">Email</FormLabel>
              <Input color="white" type="email" placeholder="Digite seu e-mail" value={whatsEmail} onChange={(e) => setWhatsEmail(e.target.value)} />
            </FormControl>
            <Button bg="#25D366" color="white" _hover={{ bg: "#1da851" }} mt={5} type="submit" isFullWidth isLoading={sendingCode} loadingText="Enviando...">
              Enviar Código
            </Button>
            <Box textAlign="center" mt={4}>
              <Text
                color="gray.300" fontSize="sm" cursor="pointer"
                _hover={{ color: "white", textDecoration: "underline" }}
                onClick={() => { setMode("password"); setError(""); }}
              >
                Voltar para login com senha
              </Text>
            </Box>
          </form>
        )}

        {/* ── Digitar Código ── */}
        {mode === "whatsapp-code" && (
          <VStack spacing={4}>
            <Box textAlign="center">
              <Text color="white" fontSize="xl" fontWeight="bold">Digite o Código</Text>
              <Text color="gray.300" fontSize="xs" mt={1}>
                Enviado para o WhatsApp {phoneMasked}
              </Text>
            </Box>
            {error && (
              <Alert status="error" borderRadius="md" w="100%">
                <AlertIcon /><AlertTitle fontSize="sm">{error}</AlertTitle>
              </Alert>
            )}
            <HStack justify="center">
              <PinInput
                size="lg" type="number" placeholder=""
                value={codigo}
                onChange={setCodigo}
                onComplete={handleValidarCodigo}
                otp
              >
                <PinInputField color="white" borderColor="gray.500" _hover={{ borderColor: "orange.400" }} _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }} />
                <PinInputField color="white" borderColor="gray.500" _hover={{ borderColor: "orange.400" }} _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }} />
                <PinInputField color="white" borderColor="gray.500" _hover={{ borderColor: "orange.400" }} _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }} />
                <PinInputField color="white" borderColor="gray.500" _hover={{ borderColor: "orange.400" }} _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }} />
                <PinInputField color="white" borderColor="gray.500" _hover={{ borderColor: "orange.400" }} _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }} />
                <PinInputField color="white" borderColor="gray.500" _hover={{ borderColor: "orange.400" }} _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }} />
              </PinInput>
            </HStack>
            <Text color="gray.400" fontSize="xs">O código expira em 5 minutos</Text>
            <Button
              variant="link" color="gray.300" fontSize="sm"
              _hover={{ color: "white" }}
              onClick={handleReenviar}
              isLoading={sendingCode} loadingText="Reenviando..."
            >
              Reenviar código
            </Button>
            <Text
              color="gray.300" fontSize="sm" cursor="pointer"
              _hover={{ color: "white", textDecoration: "underline" }}
              onClick={() => { setMode("password"); setError(""); setCodigo(""); }}
            >
              Voltar para login com senha
            </Text>
          </VStack>
        )}
      </Box>
    </Flex>
  );
};

export default Login;
